import React, { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CircularProgress,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import moment from "moment";
import pulseServices from "app/services/pulse-services";
import { SourceChip } from "./LiveTab";
import { SOURCE_COLORS, timeAgo } from "./pulseFormat";

const SmallTable = ({ title, columns, rows, empty }) => (
    <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
            <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((c) => <TableCell key={c.key} align={c.align}>{c.label}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, i) => (
                            <TableRow key={i} hover>
                                {columns.map((c) => <TableCell key={c.key} align={c.align}>{c.render ? c.render(row) : row[c.key]}</TableCell>)}
                            </TableRow>
                        ))}
                        {rows.length === 0 && <TableRow><TableCell colSpan={columns.length}><Typography color="text.secondary">{empty}</Typography></TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
        </CardContent>
    </Card>
);

const UsageTab = () => {
    const [companies, setCompanies] = useState([]);
    const [companyId, setCompanyId] = useState("");
    const [days, setDays] = useState(14);
    const [data, setData] = useState(null);

    useEffect(() => {
        pulseServices.health().then((rows) => setCompanies(rows.map((r) => ({ id: r.company_id, name: r.name })))).catch(() => {});
    }, []);

    useEffect(() => {
        setData(null);
        pulseServices.usage({ days, company_id: companyId }).then(setData).catch(() => setData({ series: [], top_screens: [], top_endpoints: [], users: [] }));
    }, [days, companyId]);

    const series = (data?.series || []).map((d) => ({ ...d, label: moment(d.day).format("DD/MM") }));

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField select size="small" label="Cliente" value={companyId} onChange={(e) => setCompanyId(e.target.value)} sx={{ minWidth: 200 }}>
                    <MenuItem value="">Todos</MenuItem>
                    {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Período" value={days} onChange={(e) => setDays(e.target.value)} sx={{ minWidth: 140 }}>
                    <MenuItem value={7}>7 días</MenuItem>
                    <MenuItem value={14}>14 días</MenuItem>
                    <MenuItem value={30}>30 días</MenuItem>
                    <MenuItem value={90}>90 días</MenuItem>
                </TextField>
            </Stack>

            {!data ? (
                <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress color="warning" /></Stack>
            ) : (
                <>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6">Usuarios activos por día</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Personas distintas que usaron la app o la web ese día.</Typography>
                            <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={series} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
                                    <CartesianGrid stroke="#E5E7EB" vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="app" name="App" stroke={SOURCE_COLORS.app} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="web" name="Web" stroke={SOURCE_COLORS.web} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <SmallTable
                                title="Pantallas más usadas"
                                empty="Sin pantallas registradas todavía."
                                rows={data.top_screens}
                                columns={[
                                    { key: "screen", label: "Pantalla" },
                                    { key: "source", label: "Origen", render: (r) => <SourceChip source={r.source} /> },
                                    { key: "views", label: "Vistas", align: "right" },
                                    { key: "users", label: "Usuarios", align: "right" },
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SmallTable
                                title="Endpoints"
                                empty="Sin requests todavía."
                                rows={data.top_endpoints}
                                columns={[
                                    { key: "endpoint", label: "Endpoint" },
                                    { key: "source", label: "Origen", render: (r) => <SourceChip source={r.source} /> },
                                    { key: "requests", label: "Requests", align: "right" },
                                    { key: "avg_ms", label: "Prom. ms", align: "right", render: (r) => <Typography variant="body2" color={r.avg_ms > 3000 ? "error.main" : "text.primary"}>{r.avg_ms}</Typography> },
                                    { key: "errors", label: "Errores", align: "right", render: (r) => `${r.errors}${r.rejected ? ` +${r.rejected}` : ""}` },
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SmallTable
                                title="Personas"
                                empty="Sin usuarios en el período."
                                rows={data.users}
                                columns={[
                                    { key: "user_name", label: "Usuario" },
                                    // La primera es donde mas se movio; un superadmin que rota entre clientes trae varias.
                                    { key: "companies", label: "Empresa", render: (r) => {
                                        const list = r.companies || [];
                                        if (list.length === 0) return "-";
                                        return (
                                            <span title={list.join(", ")}>
                                                {list[0]}
                                                {list.length > 1 && <Typography component="span" variant="caption" color="text.secondary"> +{list.length - 1}</Typography>}
                                            </span>
                                        );
                                    } },
                                    { key: "active_days", label: "Días activos", align: "right" },
                                    { key: "requests", label: "Requests", align: "right" },
                                    { key: "sources", label: "Usa", render: (r) => <Stack direction="row" spacing={0.5}>{r.sources.map((s) => <SourceChip key={s} source={s} />)}</Stack> },
                                    { key: "app_versions", label: "Versiones", render: (r) => r.app_versions.join(", ") || "-" },
                                    { key: "last_at", label: "Último uso", render: (r) => timeAgo(r.last_at) },
                                ]}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
        </Stack>
    );
};

export default UsageTab;
